import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorageIcon from "@mui/icons-material/Storage";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ Blockchain Content Data
const blockchainContent = [
  {
    section: "Smart Contracts",
    icon: <AccountBalanceWalletIcon />,
    items: [
      {
        name: "Ethereum Smart Contracts",
        description: "Self-executing code on blockchain.",
        fullContent: [
          {
            heading: "Overview",
            text: `Smart contracts are self-executing programs stored on the blockchain.
They run automatically when predefined conditions are met, removing the need for intermediaries.
On Ethereum, smart contracts are deployed and executed by the Ethereum Virtual Machine (EVM).`,
          },
          {
            heading: "Example Code (Solidity)",
            code: `pragma solidity ^0.8.0;

contract SimpleStorage {
    uint public storedData;
    function set(uint x) public {
        storedData = x;
    }
    function get() public view returns (uint) {
        return storedData;
    }
}`,
            language: "solidity"
          },
        ],
      },
    ],
  },
  {
    section: "Blockchain Applications",
    icon: <StorageIcon />,
    items: [
      {
        name: "DeFi with Web3.py",
        description: "Blockchain-powered financial services in Python.",
        fullContent: [
          {
            heading: "Example Code (Python - Web3.py)",
            code: `from web3 import Web3

# Connect to Ethereum node
w3 = Web3(Web3.HTTPProvider("https://mainnet.infura.io/v3/YOUR_PROJECT_ID"))

print("Connected:", w3.is_connected())

# Get latest block
latest_block = w3.eth.block_number
print("Latest block:", latest_block)`,
            language: "python"
          },
        ],
      },
      {
        name: "DeFi with Web3.js",
        description: "Blockchain-powered financial services in JavaScript.",
        fullContent: [
          {
            heading: "Example Code (Web3.js - JavaScript)",
            code: `import Web3 from "web3";

const web3 = new Web3("https://mainnet.infura.io/v3/YOUR_PROJECT_ID");

web3.eth.getBlockNumber().then(block => {
  console.log("Latest block:", block);
});`,
            language: "javascript"
          },
        ],
      },
    ],
  },
];

function Blockchain() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Blockchain
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {blockchainContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {blockchainContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {blockchainContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {blockchainContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Blockchain;
